/**
 * useModulePaneState — the module-pane persistence/catalog callback + effect
 * cluster extracted from ChatBox (delegation pattern). The bodies + dependency
 * arrays are reproduced VERBATIM from ChatBox; this hook is a CONTIGUOUS slice
 * of what used to be inline (5 useCallbacks + 3 useEffects + 1 useRef), so hook
 * count/order is preserved exactly when ChatBox calls it at the same position
 * the block previously occupied.
 *
 * The thin useCallback wrappers delegate to the pure helpers in
 * chatbox/module-pane-helpers.ts (already unit-tested). Refs and setters are
 * threaded in BY REFERENCE so reads/writes stay shared with ChatBox.
 */
import { useCallback, useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import type { ConversationPanel, WorkspaceModule } from '@/lib/api/endpoints/workspaces';
import type { Module } from '@/types/modules';
import type { Conversation } from '@/types/conversations';
import {
  persistModulePaneState as persistModulePaneStateHelper,
  hydrateModulePaneState as hydrateModulePaneStateHelper,
  ensureModulePanelInConversation as ensureModulePanelInConversationHelper,
  ensureModulePanelInConversationOnce as ensureModulePanelInConversationOnceHelper,
  refreshWorkspaceModuleCatalog as refreshWorkspaceModuleCatalogHelper,
  loadConversationPanels as loadConversationPanelsHelper,
  refreshPanelBoundModules as refreshPanelBoundModulesHelper,
} from '../components/chatbox/module-pane-helpers';

export interface ModulePaneStatePaneState {
  isVisible: boolean;
  isFullscreen: boolean;
  anchorMessageId?: string | null;
}

export interface PersistedModulePaneState {
  moduleId: string;
  isVisible: boolean;
  isFullscreen: boolean;
  anchorMessageId: string | null;
}

export interface PendingModuleFocus {
  conversationId: string;
  moduleId: string;
  anchorMessageId: string | null;
  sequence: number;
}

export interface UseModulePaneStateDeps {
  token: string | null;
  workspaceId: string;
  activeConversation: Conversation | null;
  conversationPanelsCacheRef: MutableRefObject<Record<string, ConversationPanel[]>>;
  workspaceModuleCatalogRef: MutableRefObject<WorkspaceModule[]>;
  activeConversationIdRef: MutableRefObject<string | null>;
  moduleAttachInFlightRef: MutableRefObject<Map<string, Promise<boolean>>>;
  pendingAttachedModuleFocusQueueRef: MutableRefObject<PendingModuleFocus[]>;
  setWorkspaceModuleLabels: (labels: string[]) => void;
  setPanelBoundModules: (modules: Module[]) => void;
}

export interface UseModulePaneStateResult {
  upsertConversationPanelCache: (conversationId: string, panel: ConversationPanel) => void;
  refreshWorkspaceModuleCatalog: () => Promise<WorkspaceModule[]>;
  loadConversationPanels: (
    conversationId: string,
    options?: { forceRefresh?: boolean },
  ) => Promise<ConversationPanel[]>;
  refreshPanelBoundModules: (
    conversationId: string,
    options?: { forceRefresh?: boolean },
  ) => Promise<Module[]>;
  persistModulePaneState: (
    conversationId: string,
    moduleId: string,
    paneState: ModulePaneStatePaneState,
  ) => Promise<void>;
  hydrateModulePaneState: (conversationId: string) => Promise<PersistedModulePaneState | null>;
  ensureModulePanelInConversation: (conversationId: string, moduleId: string) => Promise<boolean>;
  ensureModulePanelInConversationOnce: (conversationId: string, moduleId: string) => Promise<boolean>;
  queuePendingModuleFocus: (focus: PendingModuleFocus) => void;
}

export function useModulePaneState(deps: UseModulePaneStateDeps): UseModulePaneStateResult {
  const {
    token,
    workspaceId,
    activeConversation,
    conversationPanelsCacheRef,
    workspaceModuleCatalogRef,
    activeConversationIdRef,
    moduleAttachInFlightRef,
    pendingAttachedModuleFocusQueueRef,
    setWorkspaceModuleLabels,
    setPanelBoundModules,
  } = deps;

  const upsertConversationPanelCache = useCallback((conversationId: string, panel: ConversationPanel) => {
    const previousPanels = conversationPanelsCacheRef.current[conversationId] || [];
    const nextPanels = previousPanels.some((existing) => existing.id === panel.id)
      ? previousPanels.map((existing) => (existing.id === panel.id ? panel : existing))
      : [...previousPanels, panel];
    conversationPanelsCacheRef.current[conversationId] = nextPanels;
  }, []);

  const refreshWorkspaceModuleCatalog = useCallback(async (): Promise<WorkspaceModule[]> => {
    return refreshWorkspaceModuleCatalogHelper({
      token,
      workspaceId,
      workspaceModuleCatalogRef,
      setWorkspaceModuleLabels,
    });
  }, [token, workspaceId]);

  useEffect(() => {
    void refreshWorkspaceModuleCatalog();
  }, [refreshWorkspaceModuleCatalog]);

  const loadConversationPanels = useCallback(async (
    conversationId: string,
    options?: { forceRefresh?: boolean },
  ): Promise<ConversationPanel[]> => {
    return loadConversationPanelsHelper(conversationId, options, {
      token,
      workspaceId,
      conversationPanelsCacheRef,
    });
  }, [token, workspaceId]);

  const refreshPanelBoundModules = useCallback(async (
    conversationId: string,
    options?: { forceRefresh?: boolean },
  ): Promise<Module[]> => {
    return refreshPanelBoundModulesHelper(conversationId, options, {
      loadConversationPanels,
      refreshWorkspaceModuleCatalog,
      workspaceModuleCatalogRef,
      activeConversationIdRef,
      setPanelBoundModules,
    });
  }, [loadConversationPanels, refreshWorkspaceModuleCatalog]);

  useEffect(() => {
    if (!activeConversation?.id) {
      activeConversationIdRef.current = null;
      setPanelBoundModules([]);
      return;
    }
    activeConversationIdRef.current = activeConversation.id;
    void refreshPanelBoundModules(activeConversation.id, { forceRefresh: true });
  }, [activeConversation?.id, refreshPanelBoundModules]);

  // Guard against concurrent panel creation for the same conversation+module
  const panelCreateInflightRef = useRef<Set<string>>(new Set());

  const persistModulePaneState = useCallback(async (
    conversationId: string,
    moduleId: string,
    paneState: { isVisible: boolean; isFullscreen: boolean; anchorMessageId?: string | null },
  ): Promise<void> => {
    return persistModulePaneStateHelper(conversationId, moduleId, paneState, {
      token,
      workspaceId,
      loadConversationPanels,
      upsertConversationPanelCache,
      panelCreateInflightRef,
      conversationPanelsCacheRef,
    });
  }, [loadConversationPanels, token, upsertConversationPanelCache, workspaceId]);

  const hydrateModulePaneState = useCallback(async (conversationId: string): Promise<{
    moduleId: string;
    isVisible: boolean;
    isFullscreen: boolean;
    anchorMessageId: string | null;
  } | null> => {
    return hydrateModulePaneStateHelper(conversationId, {
      token,
      workspaceId,
      loadConversationPanels,
    });
  }, [loadConversationPanels, token, workspaceId]);

  const ensureModulePanelInConversation = useCallback(async (
    conversationId: string,
    moduleId: string,
  ): Promise<boolean> => {
    return ensureModulePanelInConversationHelper(conversationId, moduleId, {
      token,
      workspaceId,
      loadConversationPanels,
      upsertConversationPanelCache,
    });
  }, [loadConversationPanels, token, upsertConversationPanelCache, workspaceId]);

  const ensureModulePanelInConversationOnce = useCallback(async (
    conversationId: string,
    moduleId: string,
  ): Promise<boolean> => {
    return ensureModulePanelInConversationOnceHelper(conversationId, moduleId, {
      moduleAttachInFlightRef,
      ensureModulePanelInConversation,
    });
  }, [ensureModulePanelInConversation]);

  const queuePendingModuleFocus = useCallback((focus: {
    conversationId: string;
    moduleId: string;
    anchorMessageId: string | null;
    sequence: number;
  }) => {
    const nextQueue = pendingAttachedModuleFocusQueueRef.current.filter((entry) => !(
      entry.conversationId === focus.conversationId
      && entry.moduleId === focus.moduleId
    ));
    nextQueue.push(focus);
    nextQueue.sort((left, right) => left.sequence - right.sequence);
    pendingAttachedModuleFocusQueueRef.current = nextQueue;
  }, []);

  return {
    upsertConversationPanelCache,
    refreshWorkspaceModuleCatalog,
    loadConversationPanels,
    refreshPanelBoundModules,
    persistModulePaneState,
    hydrateModulePaneState,
    ensureModulePanelInConversation,
    ensureModulePanelInConversationOnce,
    queuePendingModuleFocus,
  };
}
